import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
  type DocumentData,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import type { SlideImage, SlideImageDoc } from "@/models/interfaces";

export const MAX_SLIDE_IMAGE_BYTES = 10 * 1024 * 1024;

export interface SlideImagesState {
  accountId: number | null;
  fetchedAccountId: number | null;
  items: SlideImage[];
  fetching: boolean;
  error: string | null;
}

type RootState = unknown;

const byName = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

// Newest upload first. The images of one upload share a createdAt, so they
// stay together, by name: "Slide2.png" before "Slide10.png". Sorted on the
// client: filtering on account and ordering by createdAt together would need
// a composite index.
const newestFirst = (a: SlideImage, b: SlideImage) =>
  (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0) ||
  byName.compare(a.name, b.name);

const fileExtension = (file: File) => {
  const fromName = /\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1];
  const fromType = file.type.split("/")[1]?.replace(/[^a-z0-9]/gi, "");
  return (fromName || fromType || "img").toLowerCase();
};

// generateThumbnail (functions/index.js) writes a thumb_ copy beside every
// uploaded image, so that goes too. Best effort: a file left behind costs
// storage, nothing else.
const removeStoredImage = (storagePath: string) => {
  const storage = getStorage();
  const slash = storagePath.lastIndexOf("/");
  const thumb = `${storagePath.slice(0, slash + 1)}thumb_${storagePath.slice(slash + 1)}`;
  return Promise.allSettled(
    [storagePath, thumb].map((path) => deleteObject(storageRef(storage, path))),
  );
};

const state = (): SlideImagesState => ({
  accountId: null,
  fetchedAccountId: null,
  items: [],
  fetching: false,
  error: null,
});

const mutations = {
  SET_ACCOUNT_ID(s: SlideImagesState, id: number | null) {
    s.accountId = id;
  },
  SET_FETCHED_ACCOUNT_ID(s: SlideImagesState, id: number | null) {
    s.fetchedAccountId = id;
  },
  SET_FETCHING(s: SlideImagesState, v: boolean) {
    s.fetching = v;
  },
  SET_ERROR(s: SlideImagesState, msg: string | null) {
    s.error = msg;
  },
  SET_ITEMS(s: SlideImagesState, items: SlideImage[]) {
    s.items = [...items].sort(newestFirst);
  },
  UPSERT_ITEM(s: SlideImagesState, image: SlideImage) {
    s.items = [...s.items.filter((item) => item.id !== image.id), image].sort(
      newestFirst,
    );
  },
  REMOVE_ITEM(s: SlideImagesState, id: string) {
    s.items = s.items.filter((item) => item.id !== id);
  },
};

const actions = {
  setAccountId(
    { state, commit }: ActionContext<SlideImagesState, RootState>,
    id: number | string | null | undefined,
  ) {
    // Some user docs store the account as a string.
    const accountId = id == null || id === "" ? null : Number(id);
    if (state.accountId !== accountId) {
      commit("SET_ACCOUNT_ID", accountId);
      commit("SET_FETCHED_ACCOUNT_ID", null);
      commit("SET_ITEMS", []);
    }
  },

  async fetchAll(
    { state, commit }: ActionContext<SlideImagesState, RootState>,
    options: { force?: boolean } = {},
  ) {
    if (state.accountId == null) {
      commit("SET_ITEMS", []);
      return;
    }
    if (
      !options.force &&
      (state.fetching || state.fetchedAccountId === state.accountId)
    )
      return;

    const accountId = state.accountId;
    commit("SET_FETCHING", true);
    commit("SET_ERROR", null);
    try {
      const snap = await getDocs(
        query(collection(db, "slideImages"), where("account", "==", accountId)),
      );
      // The account may have changed while the query was in flight.
      if (state.accountId !== accountId) return;

      const images: SlideImage[] = snap.docs.map((d) => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          account: data.account as number,
          url: (data.url as string) ?? "",
          storagePath: (data.storagePath as string) ?? "",
          name: (data.name as string) ?? "",
          createdBy: (data.createdBy as string) ?? "",
          createdAt: data.createdAt,
        };
      });
      commit("SET_ITEMS", images);
      commit("SET_FETCHED_ACCOUNT_ID", accountId);
    } catch (err: any) {
      commit("SET_ERROR", err?.message ?? String(err));
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  /**
   * Adds images to the library: each file to Storage, then its document.
   * Answers with one entry per file, in the files' order, null for a file
   * that failed. The others are kept when one does.
   */
  async upload(
    { state, commit }: ActionContext<SlideImagesState, RootState>,
    payload: {
      files: File[];
      createdBy: string;
      onProgress?: (done: number, total: number) => void;
    },
  ): Promise<(SlideImage | null)[]> {
    if (state.accountId == null) throw new Error("No accountId set");
    const account = state.accountId;
    const storage = getStorage();
    const total = payload.files.length;
    // One time for the whole upload, which is what keeps it together.
    const createdAt = Timestamp.now();
    let done = 0;
    payload.onProgress?.(done, total);

    const results = await Promise.allSettled(
      payload.files.map(async (file): Promise<SlideImage> => {
        // The id comes first, so the file can be named after it.
        const ref = doc(collection(db, "slideImages"));
        const storagePath = `slideshows/${account}/images/${ref.id}.${fileExtension(file)}`;
        const fileRef = storageRef(storage, storagePath);
        // The id in the name makes the file immutable, so it can be cached.
        await uploadBytes(fileRef, file, {
          cacheControl: "public, max-age=31536000, immutable",
        });
        try {
          const docData: SlideImageDoc = {
            account,
            url: await getDownloadURL(fileRef),
            storagePath,
            name: file.name,
            createdBy: payload.createdBy,
            createdAt,
          };
          await setDoc(ref, docData);
          const image: SlideImage = { id: ref.id, ...docData };
          commit("UPSERT_ITEM", image);
          payload.onProgress?.(++done, total);
          return image;
        } catch (error) {
          // Without its document the file would be out of anyone's reach.
          await removeStoredImage(storagePath);
          throw error;
        }
      }),
    );

    return results.map((result) => {
      if (result.status === "fulfilled") return result.value;
      console.error("Failed to upload slide image:", result.reason);
      return null;
    });
  },

  /**
   * Deletes an image and its file. Slides showing it would be left with a
   * dead link, so the caller checks that none do.
   */
  async remove(
    { state, commit }: ActionContext<SlideImagesState, RootState>,
    id: string,
  ) {
    const existing = state.items.find((item) => item.id === id);
    await deleteDoc(doc(db, "slideImages", id));
    commit("REMOVE_ITEM", id);
    if (existing?.storagePath) await removeStoredImage(existing.storagePath);
  },
};

const getters = {
  images(state: SlideImagesState): SlideImage[] {
    return state.items;
  },
  hasFetched(state: SlideImagesState): boolean {
    return (
      state.accountId != null && state.fetchedAccountId === state.accountId
    );
  },
  isFetching(state: SlideImagesState): boolean {
    return state.fetching;
  },
  error(state: SlideImagesState): string | null {
    return state.error;
  },
};

export const slideImages: Module<SlideImagesState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default slideImages;
